import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, buildWaLink, inr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PushToggle } from "@/components/admin/PushToggle";
import { Bell, Search, UsersRound, Ticket, Receipt, Map, MessageCircle, CalendarClock, UserPlus, CheckCheck } from "lucide-react";

const GROUPS = [
  ["leads", "Leads", UsersRound, (x) => `${x.lead_id || ""} ${x.name}`, (x) => `${x.whatsapp} · ${x.destination || "—"} · ${x.label || x.status}`, () => "/admin/leads"],
  ["bookings", "Bookings", Ticket, (x) => `${x.booking_id || "Pending"} ${x.name}`, (x) => `${x.trip_name} · ${x.departure_date} · ${x.status}`, (x) => `/admin/bookings?q=${encodeURIComponent(x.booking_id || x.name)}`],
  ["invoices", "Invoices", Receipt, (x) => x.invoice_no, (x) => `${x.customer?.name} · ${inr(x.final_total)} · ${x.payment_status}`, (x) => `/admin/invoices/${x.id}`],
  ["trips", "Trips", Map, (x) => x.name, (x) => x.destination, (x) => `/trips/${x.slug}`],
];

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const box = useRef(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`).then(({ data }) => { setRes(data); setOpen(true); }), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const h = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const total = res ? GROUPS.reduce((a, [k]) => a + (res[k]?.length || 0), 0) : 0;
  return (
    <div ref={box} className="relative flex-1 max-w-xl">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input className="pl-9 h-9 rounded-full bg-muted/50" placeholder="Search leads, bookings, invoices, trips…" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)} data-testid="global-search" />
      {open && res && (
        <div className="absolute z-50 mt-2 w-full rounded-xl border bg-card shadow-xl max-h-[70vh] overflow-auto p-2" data-testid="global-search-results">
          {total === 0 && <p className="p-3 text-sm text-muted-foreground">No matches for “{q}”</p>}
          {GROUPS.map(([k, label, Icon, title, sub, href]) => !!res[k]?.length && (
            <div key={k} className="mb-1">
              <p className="px-2 py-1 text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
              {res[k].map((x) => (
                <button key={x.id} type="button" onClick={() => { setOpen(false); setQ(""); nav(href(x)); }} className="w-full text-left rounded-lg px-2 py-1.5 hover:bg-muted text-sm" data-testid={`search-result-${k}`}>
                  <p className="font-medium truncate">{title(x)}</p><p className="text-xs text-muted-foreground truncate">{sub(x)}</p>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ICON = { followup_due: CalendarClock, booking_reminder: CalendarClock, lead_assigned: UserPlus, booking_assigned: Ticket, payment_received: Receipt };
const HREF = { followup_due: () => "/admin/leads?due=1", booking_reminder: () => "/admin/bookings", lead_assigned: () => "/admin/leads", booking_assigned: () => "/admin/bookings", payment_received: () => "/admin/payments" };

export function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/staff/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  const readAll = async () => { await api.post("/staff/notifications/read"); load(); };
  const openItem = async (n) => {
    if (!n.id.startsWith("fu-") && !n.read) await api.post(`/staff/notifications/${n.id}/read`).catch(() => {});
    setOpen(false); load(); nav(HREF[n.type]?.(n) || "/admin");
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notification-count">{data.unread > 99 ? "99+" : data.unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] max-w-[92vw] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <p className="font-medium">Notifications {data.unread > 0 && <span className="text-xs text-muted-foreground">· {data.unread} unread</span>}</p>
          <div className="flex items-center gap-1"><PushToggle compact />
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={readAll} data-testid="notifications-read-all"><CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read</Button></div>
        </div>
        <div className="max-h-[60vh] overflow-auto divide-y">
          {data.items.map((n) => { const Icon = ICON[n.type] || Bell; return (
            <div key={n.id} className={`px-4 py-3 text-sm flex gap-3 ${n.read ? "opacity-60" : ""}`} data-testid="notification-item">
              <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${n.type === "booking_reminder" ? "text-amber-500" : n.type === "followup_due" ? "text-red-500" : "text-primary"}`} />
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => openItem(n)} className="text-left w-full"><p className="font-medium leading-tight">{n.title}</p><p className="text-xs text-muted-foreground mt-0.5">{n.message}</p></button>
                {n.type === "booking_reminder" && n.whatsapp && (
                  <a href={buildWaLink(n.whatsapp, `Hi! Reminder from Safarshala: your trip departs in ${n.days} day${n.days > 1 ? "s" : ""}. Booking ${n.booking_id || ""}. Please be ready with your documents. See you soon!`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1.5 text-xs text-emerald-600 dark:text-emerald-400 font-medium" data-testid="reminder-whatsapp">
                    <MessageCircle className="h-3.5 w-3.5" /> Send reminder on WhatsApp
                  </a>
                )}
              </div>
            </div>
          ); })}
          {!data.items.length && <p className="p-6 text-center text-sm text-muted-foreground">You're all caught up.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
